import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ExternalLink, Shuffle } from "lucide-react";
import { Reveal } from "./motion";
import { usePlayer } from "../../context/SpotifyPlayerContext";

const Equalizer = () => (
  <div className="flex items-end gap-[2px] h-3.5" aria-hidden>
    {[0, 1, 2, 3].map((i) => (
      <motion.span
        key={i}
        className="w-[2px] bg-[#FAFAFA]"
        animate={{ height: ["20%", "100%", "40%", "90%", "30%"] }}
        transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: "easeInOut" }}
      />
    ))}
  </div>
);

export default function Music({ music }) {
  const player = usePlayer();
  const playlists = music?.playlists || [];
  const [activeIdx, setActiveIdx] = useState(0);
  const active = playlists[activeIdx];
  const current = player?.current;
  const isPlaying = player?.isPlaying;

  return (
    <section id="music" data-testid="music-section" className="relative py-28 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.28em] text-[#7a7a7a]">/ 03 — Music</span>
          <h2 className="font-serif text-5xl md:text-7xl tracking-tighter text-[#FAFAFA] mt-5 leading-[0.88]">
            Music, a <span className="italic font-light text-[#bdbdbd]">language</span>
          </h2>
          <p className="text-[#8f8f8f] max-w-lg mt-5">
            Songs I live by, in four playlists for every mood. Press shuffle, or dive into one.
          </p>
          <button
            data-testid="shuffle-all-btn"
            onClick={() => player.shuffleAll(playlists)}
            className="group mt-7 inline-flex items-center gap-2.5 bg-[#FAFAFA] text-[#050505] px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1DB954] hover:text-[#050505] transition-colors duration-300"
          >
            <Shuffle size={15} className="transition-transform duration-300 group-hover:rotate-6" />
            Shuffle all playlists
          </button>
        </Reveal>

        {/* Playlist selector */}
        <Reveal delay={0.05}>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {playlists.map((p, i) => (
              <button
                key={p.id}
                data-testid={`playlist-tab-${p.id}`}
                onClick={() => setActiveIdx(i)}
                className={`group relative overflow-hidden border text-left transition-colors duration-300 ${
                  i === activeIdx ? "border-[#FAFAFA]" : "border-[#1c1c1c] hover:border-[#3a3a3a]"
                }`}
              >
                <div className="aspect-square overflow-hidden bg-[#0d0d0d]">
                  <img
                    src={p.cover}
                    alt={p.title}
                    loading="lazy"
                    className={`h-full w-full object-cover transition-all duration-700 group-hover:scale-105 ${
                      i === activeIdx ? "grayscale-0" : "grayscale"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="font-serif text-lg md:text-xl text-[#FAFAFA] leading-tight">{p.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#a3a3a3]">
                    {p.tracks.length} tracks
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Reveal>

        {/* Tracklist for active playlist */}
        <div className="mt-8 border border-[#1c1c1c] bg-[#080808]">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 p-6 md:p-8 border-b border-[#1c1c1c]">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7a7a7a]">Playlist</span>
              <h3 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#FAFAFA] mt-2 leading-none">
                {active?.title}
              </h3>
              <p className="text-[#8f8f8f] mt-3 max-w-md">{active?.description}</p>
            </div>
            {active && (
              <a
                href={`https://open.spotify.com/playlist/${active.spotify_playlist_id}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`open-spotify-${active.id}`}
                className="inline-flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-[#FAFAFA] border border-[#2f2f2f] hover:border-[#FAFAFA] transition-colors self-start"
              >
                Full playlist on Spotify <ExternalLink size={13} />
              </a>
            )}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={active?.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="p-3 md:p-4"
            >
              <div className="grid grid-cols-[32px_1fr_auto] md:grid-cols-[40px_1fr_200px_100px] gap-4 px-3 md:px-4 py-3 border-b border-[#1c1c1c] font-mono text-[10px] uppercase tracking-[0.22em] text-[#6a6a6a]">
                <span>#</span>
                <span>Title</span>
                <span className="hidden md:block">Album</span>
                <span className="text-right">Source</span>
              </div>

              {active?.tracks.map((t, i) => {
                const isActive = current && current.spotify_id === t.spotify_id && current.title === t.title;
                return (
                  <button
                    key={`${t.spotify_id}-${i}`}
                    data-testid={`track-row-${active.id}-${i}`}
                    onClick={() => player.playTrack(t, active)}
                    className={`group grid grid-cols-[32px_1fr_auto] md:grid-cols-[40px_1fr_200px_100px] gap-4 items-center w-full text-left px-3 md:px-4 py-3 rounded-sm transition-colors duration-300 ${
                      isActive ? "bg-[#161616]" : "hover:bg-[#111111]"
                    }`}
                  >
                    <span className="flex items-center justify-center w-6">
                      {isActive && isPlaying ? (
                        <Equalizer />
                      ) : (
                        <>
                          <span className="font-mono text-sm text-[#7a7a7a] group-hover:hidden">{i + 1}</span>
                          <Play size={14} fill="currentColor" className="hidden group-hover:block text-[#FAFAFA]" />
                        </>
                      )}
                    </span>
                    <span className="min-w-0">
                      <span className={`block font-medium truncate ${isActive ? "text-[#FAFAFA]" : "text-[#e6e6e6]"}`}>
                        {t.title}
                      </span>
                      <span className="block text-sm text-[#8a8a8a] truncate">{t.artist}</span>
                    </span>
                    <span className="hidden md:block text-sm text-[#8a8a8a] truncate">{t.album}</span>
                    <span className="text-right font-mono text-[10px] uppercase tracking-[0.18em] text-[#7a7a7a] group-hover:text-[#c9c9c9] transition-colors">
                      Spotify
                    </span>
                  </button>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
